"use client"

import { useCallback, useEffect, useState } from "react"
import { ArrowUpRight } from "lucide-react"
import Image from "next/image"
import { apiUrl, authHeaders } from "@/lib/api"
import FetchError from "./fetch-error"

interface StoreObj { id: number; name: string }
interface Produto {
  id: number
  title: string
  store: StoreObj
  discount_percentage?: number
  price_current?: string
  price_original?: string
  image_url?: string
  url?: string
}

function formatPrice(val?: string | number): string {
  if (!val) return "—"
  const n = typeof val === "string" ? parseFloat(val) : val
  if (isNaN(n) || n <= 0) return "—"
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

export default function PopularArticles() {
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    fetch(`${apiUrl()}/products/best_deals/`, { headers: authHeaders(token) })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        const lista = data.results || (Array.isArray(data) ? data : [])
        setProdutos(lista.slice(0, 8))
        setError(false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="surface-card">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Ranking</div>
          <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
            Maiores descontos
          </h3>
        </div>
        <a
          href="/dashboard-cms/produtos"
          className="group inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Ver todos
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            {[...Array(8)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded bg-muted/30" />)}
          </div>
        ) : error && produtos.length === 0 ? (
          <FetchError what="o ranking de descontos" onRetry={load} />
        ) : produtos.length === 0 ? (
          <div className="py-8 text-sm text-muted-foreground">Nenhum desconto relevante capturado ainda hoje.</div>
        ) : (
          <div className="grid grid-cols-1 gap-x-8 gap-y-1 lg:grid-cols-2">
            {produtos.map((p, index) => {
              const lojaNome = p.store?.name || ""
              const desconto = p.discount_percentage ?? 0
              const economia = p.price_original && p.price_current
                ? parseFloat(p.price_original) - parseFloat(p.price_current)
                : 0
              return (
                <a
                  key={p.id}
                  href={p.url || "#"}
                  target={p.url ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group -mx-2 flex items-center gap-3 rounded-md px-2 py-3 transition-colors hover:bg-muted/30"
                >
                  <span className="w-6 flex-shrink-0 text-right font-mono text-[10px] text-muted-foreground tabular-nums">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border border-border/40 bg-muted/30">
                    {p.image_url ? (
                      <Image src={p.image_url} alt={p.title} fill className="object-contain p-1" unoptimized />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">·</div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-medium text-foreground">{p.title}</h4>
                    <div className="mt-0.5 flex items-baseline gap-2 text-xs text-muted-foreground">
                      {lojaNome && <span className="text-foreground/70">{lojaNome}</span>}
                      {economia > 0 && (
                        <>
                          <span>·</span>
                          <span className="tabular-nums">economia {formatPrice(economia)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    <div className={`font-mono text-base font-semibold tracking-tight ${desconto >= 60 ? "text-rose-500 dark:text-rose-400" : "text-[var(--brand-amber)]"}`}>
                      −{desconto}%
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground">{formatPrice(p.price_current)}</div>
                  </div>
                </a>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
